import {
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { PaginationMeta, PaginationMetaLink } from '../models/Paginator';
import api from '../services/api';
import ModalComponent from '../components/ModalComponent';
import FileManagerUtils from '../utils/FileManagerUtils';

export interface BasicFileInterface {
  file_name: string
  id?: number
  path?: string
  size?: number
  extension?: string
}

export type FileInterface = BasicFileInterface;

interface FileContextProps {
  total: number
  files?: FileInterface[]
  currentPage?: number
  links?: PaginationMetaLink[]
  paginationPageInfo?: PaginationMeta
  errors?: any
  create?: (file: File, cb?: () => void, cbe?: () => void) => void
  edit?: (fileId: number, file: File, cb?: () => void) => void
  deleteFile?: (fileId: number, cb?: () => void) => void
  fetch?: (page?: number) => void
  getDownloaddableFileUrl?: (fileName: string) => string
  isLoading?: boolean,
}

export const FileContext = createContext<FileContextProps>({ total: 0 });

interface Props {
  children: JSX.Element
}

function FilesProvider({ children }: Props) {
  const [total, setTotal] = useState(0);
  const [links, setLinks] = useState(null);
  const [files, setFiles] = useState([] as FileInterface[]);
  const [currentPage] = useState(1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [paginationPageInfo, setPaginationPageInfo] = useState({
    current_page: 1,
    from: 1,
    last_page: 1,
    links: [],
    path: '',
    per_page: 1,
    to: 1,
    total: 1,
  });

  // eslint-disable-next-line no-spaced-func
  const [modalProperties, setModalProperties] = useState<{
    isOpen: boolean,
    fn: () => void
  }>({ isOpen: false, fn: () => { } });

  const fetch = useCallback(async (page?: number) => {
    setIsLoading(true);

    try {
      const result = await api.get(`/v1/files${page ? `?page=${page}` : ''}`);

      setPaginationPageInfo(result.data.meta);
      setTotal(result.data.meta.total);
      setLinks(result.data.meta.links);
      setFiles(result.data.data);
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const overwrite = useCallback((file: File, existingFileId: number, cb: () => void) => {
    try {
      const formData = new FormData();
      formData.append('file', file as any);

      api.post(`/v1/files/edit/${existingFileId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(() => {
        cb();
        setModalProperties({ ...modalProperties, isOpen: false });
        fetch(currentPage);
      });
    } catch {
      console.error('Failed to overwrite file');
    }
  }, [currentPage, fetch, modalProperties]);

  const create = useCallback(async (
    file: File,
    cb?: () => void,
    cbe?: () => void,
  ) => {
    if (!FileManagerUtils.checkFileExtension(file, setErrors)) return;

    setIsLoading(true);

    FileManagerUtils.checkIfFileExists(
      '/v1/files',
      file.name,
      async () => {
        try {
          const formData = new FormData();
          formData.append('file', file as any);
          await api.post('/v1/files/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then(() => {
            if (cb) cb();
            fetch(currentPage);
          });
        } catch (e: any) {
          const { data } = e.response;
          setErrors(data.errors);
          if (cbe) cbe();
          console.error(e);
        } finally {
          setIsLoading(false);
        }
      },
      setModalProperties,
      (fileId) => overwrite(file, fileId, cb),
    );
  }, [currentPage, fetch, overwrite]);

  const edit = useCallback(async (
    fileId: number,
    file: File,
    cb?: () => void,
  ) => {
    if (!FileManagerUtils.checkFileExtension(file, setErrors)) return;

    setIsLoading(true);

    FileManagerUtils.checkIfFileExists(
      '/v1/files',
      file.name,
      async () => {
        try {
          const formData = new FormData();
          formData.append('file', file as any);

          await api.post(`/v1/files/edit/${fileId}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then(() => {
            if (cb) cb();
            fetch(currentPage);
          });
        } catch (e) {
          console.error(e);
        } finally {
          setIsLoading(false);
        }
      },
      setModalProperties,
      () => overwrite(file, fileId, cb),
    );
  }, [currentPage, fetch, overwrite]);

  const deleteFile = useCallback(async (fileId: number, cb?: () => void) => {
    setIsLoading(true);
    try {
      await api.delete(`/v1/files/${fileId}`)
        .then(() => {
          if (cb) cb();
          fetch(currentPage);
        });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, fetch]);

  const getDownloaddableFileUrl = useCallback((fileName: string) => `/files/${fileName}`, []);

  const value = useMemo<FileContextProps>(() => ({
    total,
    links,
    files,
    currentPage,
    paginationPageInfo,
    errors,
    create,
    fetch,
    edit,
    deleteFile,
    getDownloaddableFileUrl,
    isLoading,
  }), [total, links, files, currentPage, paginationPageInfo, errors, create, fetch, edit, deleteFile, getDownloaddableFileUrl, isLoading]);

  return (
    <>
      <FileContext.Provider value={value}>
        {children}
      </FileContext.Provider>

      <ModalComponent
        centered
        title="確認"
        show={modalProperties.isOpen}
        onConfirm={modalProperties.fn}
        confirmButtonText="上書き"
        closeModal={() => { setModalProperties({ ...modalProperties, isOpen: false }); setIsLoading(false); }}
        backdropClassName="z-index-3000"
        style={{ zIndex: 3000 }}
      >
        ファイルを上書きします。よろしいですか？
      </ModalComponent>
    </>
  );
}

export default FilesProvider;
